import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import getAPIClient from "../hooks/apiClient";
import Header from "../elements/Header";
import InlineNotification from "../elements/InlineNotification";
import OneSelectUI from "../elements/inputs/one-select";
import SubmitButton from "../elements/inputs/SubmitButton";
import s from "../styles/pages/resume_batch.module.scss";

const STORAGE_KEY_PREFIX = 'resume-selection-stage-filter-';
const ALL_DIRECTIONS_VALUE = '__ALL_DIRECTIONS__';
const ALL_NON_FINAL_STAGES_VALUE = '__ALL_NON_FINAL_STAGES__';
const ALL_DIRECTIONS_OPTION = {value: ALL_DIRECTIONS_VALUE, direction_id: null, label: 'Все направления'};
const ALL_NON_FINAL_STAGES_OPTION = {value: ALL_NON_FINAL_STAGES_VALUE, stage_id: null, label: 'Все нефинальные этапы'};

const isAllDirections = (option) => !!option && option.value === ALL_DIRECTIONS_VALUE;
const hasAllNonFinalSentinel = (stages) => Array.isArray(stages) && stages.some((s) => s && s.value === ALL_NON_FINAL_STAGES_VALUE);

function safeNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function parsePlacementOptions(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return null; }
}

function getStatusMeta(status) {
    if (status === 'created') return {label: 'Оценено', className: s.statusCreated};
    if (status === 'creating') return {label: 'Оценивается', className: s.statusProcessing};
    if (status === 'updating') return {label: 'Переоценивается', className: s.statusProcessing};
    if (status === 'error') return {label: 'Ошибка оценки', className: s.statusError};
    return {label: 'Без оценки', className: s.statusPending};
}

function getScoreMeta(score) {
    if (score === undefined || score === null) {
        return {value: '--', color: '#CBD5E1', progressDeg: 0};
    }
    const normalized = Math.max(1, Math.min(10, Number(score)));
    let color = '#EF4444';
    if (normalized >= 8) color = '#22C55E';
    else if (normalized >= 6) color = '#F59E0B';
    return {value: normalized, color, progressDeg: normalized * 36};
}

function toSortableScore(score) {
    if (score === undefined || score === null || score === '') return null;
    const parsed = Number(score);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(1, Math.min(10, parsed));
}

function buildFullName(item) {
    const fullName = (item.full_name ?? '').trim();
    if (fullName.length > 0) return fullName;
    const parts = [item.last_name, item.first_name, item.middle_name]
        .map((v) => (v ?? '').trim()).filter((v) => v.length > 0);
    if (parts.length) return parts.join(' ');
    return item.title || `Резюме #${item.crm_item_id}`;
}

async function resolveVacancyId(BX24, searchParams) {
    const fromQuery = safeNumber(searchParams.get('vacancy_id'));
    if (fromQuery) return fromQuery;
    try {
        const placement = await BX24.placement.info();
        const raw = placement?.options?.ID || placement?.options?.ENTITY_ID || placement?.options?.entityId;
        const normalized = safeNumber(raw);
        if (normalized) return normalized;
    } catch (_) {}
    const allParams = BX24?.getAllParams?.() || {};
    const direct = safeNumber(allParams.ID || allParams.ENTITY_ID || allParams.entityId);
    if (direct) return direct;
    const parsed = parsePlacementOptions(allParams.PLACEMENT_OPTIONS || allParams.placement_options);
    const fromParsed = safeNumber(parsed?.ID || parsed?.ENTITY_ID || parsed?.entityId);
    if (fromParsed) return fromParsed;
    return null;
}

export default function ResumeWorkspacePage({BX24}) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // --- loading ---
    const [metaLoading, setMetaLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // --- notifications ---
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');

    // --- core data ---
    const [vacancyId, setVacancyId] = useState(null);
    const [directions, setDirections] = useState([]);
    const [allStagesByDirection, setAllStagesByDirection] = useState({});
    const [portalSettings, setPortalSettings] = useState(null);

    // --- filters ---
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedStages, setSelectedStages] = useState([]);
    const [filtersInitialized, setFiltersInitialized] = useState(false);
    // becomes true as soon as the user explicitly picks any filter option
    // (including sentinel "all directions" / "all non-final stages")
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [hideEvaluated, setHideEvaluated] = useState(false);
    const [threshold, setThreshold] = useState({from: '', to: ''});

    // --- resumes ---
    const [resumes, setResumes] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedIds, setExpandedIds] = useState(new Set());

    // --- evaluation results & polling ---
    const [resultById, setResultById] = useState({});
    const [evaluatingIds, setEvaluatingIds] = useState([]);
    const pollRef = useRef(null);

    // --- stage movement ---
    const [selectedTargetStage, setSelectedTargetStage] = useState(null);

    const handleError = useCallback((defaultText, e) => {
        console.error(defaultText, e);
        setNotificationType('error');
        setNotificationText(e?.response?.data?.detail || defaultText);
    }, []);

    // --- Vacancy ID resolution ---
    useEffect(() => {
        const init = async () => {
            setMetaLoading(true);
            try {

                const id = await resolveVacancyId(BX24, searchParams);
                if (!id) {
                    setNotificationType('error');
                    setNotificationText('Не удалось получить ID вакансии из контекста карточки');
                    setMetaLoading(false);
                    return;
                }
                setVacancyId(id);
            } catch (e) {
                handleError('Ошибка инициализации', e);
            }
        };
        init();
    }, [BX24, searchParams, handleError]);

    // --- Metadata loading ---
    useEffect(() => {
        const loadMeta = async () => {
            if (!vacancyId) return;
            setFiltersInitialized(false);
            try {
                const directionsResponse = await api.get('/analysis_agent/applicant/directions');
                const directionOptions = (directionsResponse.data || []).map((d) => ({
                    ...d, value: d.direction_id, label: d.direction_name,
                }));
                setDirections(directionOptions);

                const stagesMap = {};
                for (const direction of directionOptions) {
                    const params = {};
                    if (direction.direction_id !== 0) params.direction_id = direction.direction_id;
                    const stagesResponse = await api.get('/analysis_agent/applicant/stages', {params});
                    stagesMap[direction.direction_id] = (stagesResponse.data || []).map((stage) => ({
                        ...stage,
                        direction_id: direction.direction_id,
                        value: stage.stage_id,
                        label: `${stage.stage_name}${stage.is_final ? ' (финальный)' : ''}`,
                    }));
                }
                setAllStagesByDirection(stagesMap);

                const settingsResponse = await api.get('/bitrix_portals/settings');
                setPortalSettings(settingsResponse.data || null);

                // Restore filters: URL has priority over localStorage
                const storageKey = `${STORAGE_KEY_PREFIX}${vacancyId}`;
                const urlDirectionId = searchParams.get('direction_id');
                const urlStageIds = searchParams.get('stage_ids');
                const allStagesFlat = Object.values(stagesMap).flat();
                if (urlDirectionId !== null || urlStageIds !== null) {
                    const urlDirNumeric = safeNumber(urlDirectionId);
                    if (urlDirNumeric !== null) {
                        const direction = directionOptions.find((item) => Number(item.direction_id) === urlDirNumeric);
                        if (direction) setSelectedDirection(direction);
                    }
                    if (urlStageIds) {
                        const ids = urlStageIds.split(',').map((id) => id.trim()).filter(Boolean);
                        const stagesFromUrl = allStagesFlat.filter((stage) => ids.includes(String(stage.stage_id)));
                        if (stagesFromUrl.length) setSelectedStages(stagesFromUrl);
                    }
                    setFiltersApplied(true);
                } else {
                    const persistedRaw = localStorage.getItem(storageKey);
                    if (persistedRaw) {
                    let persistedDirectionId = null;
                    let persistedDirectionIsAll = false;
                    let persistedStageIds = [];
                    let persistedStagesIsAllNonFinal = false;
                    try {
                        const parsed = JSON.parse(persistedRaw);
                        if (Array.isArray(parsed)) {
                            persistedStageIds = parsed.map((id) => String(id));
                        } else if (parsed && typeof parsed === 'object') {
                            if (parsed.direction_id === ALL_DIRECTIONS_VALUE) {
                                persistedDirectionIsAll = true;
                            } else if (Array.isArray(parsed.direction_ids) && parsed.direction_ids.length > 0) {
                                persistedDirectionId = safeNumber(parsed.direction_ids[0]);
                            } else {
                                persistedDirectionId = safeNumber(parsed.direction_id);
                            }
                            if (Array.isArray(parsed.stage_ids)) {
                                if (parsed.stage_ids.includes(ALL_NON_FINAL_STAGES_VALUE)) {
                                    persistedStagesIsAllNonFinal = true;
                                } else {
                                    persistedStageIds = parsed.stage_ids.map((id) => String(id));
                                }
                            }
                        }
                    } catch (_) {
                        persistedStageIds = [String(persistedRaw)];
                    }
                    const allStages = Object.values(stagesMap).flat();
                    const persistedStages = allStages.filter((stage) => persistedStageIds.includes(String(stage.stage_id)));
                    if (persistedStages.length || persistedDirectionId !== null || persistedDirectionIsAll || persistedStagesIsAllNonFinal) {
                        setFiltersApplied(true);
                    }
                    if (persistedStagesIsAllNonFinal) {
                        setSelectedStages([ALL_NON_FINAL_STAGES_OPTION]);
                    } else if (persistedStages.length) {
                        setSelectedStages(persistedStages);
                    }
                    if (persistedDirectionIsAll) {
                        setSelectedDirection(ALL_DIRECTIONS_OPTION);
                    } else if (persistedDirectionId !== null) {
                        const direction = directionOptions.find((item) => Number(item.direction_id) === persistedDirectionId);
                        if (direction) setSelectedDirection(direction);
                    } else if (persistedStages.length) {
                        const singleDirId = persistedStages[0]?.direction_id;
                        const oneDir = persistedStages.every((st) => String(st.direction_id) === String(singleDirId));
                        if (oneDir) {
                            const direction = directionOptions.find((item) => String(item.direction_id) === String(singleDirId));
                            if (direction) setSelectedDirection(direction);
                        }
                    }
                    }
                }
            } catch (e) {
                handleError('Ошибка загрузки направлений/этапов', e);
            } finally {
                setFiltersInitialized(true);
                setMetaLoading(false);
            }
        };
        loadMeta();
    }, [api, vacancyId, handleError, searchParams]);

    // --- Filter persistence ---
    useEffect(() => {
        if (!vacancyId || !filtersInitialized) return;
        const storageKey = `${STORAGE_KEY_PREFIX}${vacancyId}`;
        if (!selectedDirection && selectedStages.length === 0) {
            localStorage.removeItem(storageKey);
            return;
        }
        const directionPayload = isAllDirections(selectedDirection)
            ? ALL_DIRECTIONS_VALUE
            : (selectedDirection?.direction_id ?? null);
        const stagesPayload = hasAllNonFinalSentinel(selectedStages)
            ? [ALL_NON_FINAL_STAGES_VALUE]
            : selectedStages.map((stage) => stage.stage_id);
        localStorage.setItem(storageKey, JSON.stringify({
            direction_id: directionPayload,
            stage_ids: stagesPayload,
        }));
    }, [vacancyId, selectedDirection, selectedStages, filtersInitialized]);

    // --- Computed filter values ---
    const visibleStages = useMemo(() => {
        if (selectedDirection && !isAllDirections(selectedDirection)) {
            return allStagesByDirection[selectedDirection.direction_id] || [];
        }
        return Object.values(allStagesByDirection).flat();
    }, [selectedDirection, allStagesByDirection]);

    const nonFinalStageIds = useMemo(() => {
        return new Set(visibleStages.filter((stage) => !stage.is_final).map((stage) => stage.stage_id));
    }, [visibleStages]);

    // Sentinel-опции трактуем как "нет фильтра" в логике — но сам sentinel остаётся в state,
    // чтобы он отображался в селекте (иначе пользователь видит placeholder "Выберите…").
    const effectiveDirection = useMemo(
        () => (isAllDirections(selectedDirection) ? null : selectedDirection),
        [selectedDirection]
    );
    const effectiveStages = useMemo(
        () => (hasAllNonFinalSentinel(selectedStages) ? [] : selectedStages),
        [selectedStages]
    );

    const selectedStageIds = useMemo(() => {
        return new Set(effectiveStages.map((stage) => stage.stage_id));
    }, [effectiveStages]);

    const directionOptionsWithAll = useMemo(() => (
        directions.length
            ? [{value: ALL_DIRECTIONS_VALUE, direction_id: null, label: 'Все направления'}, ...directions]
            : directions
    ), [directions]);

    const stageOptionsWithAll = useMemo(() => (
        visibleStages.length
            ? [{value: ALL_NON_FINAL_STAGES_VALUE, stage_id: null, label: 'Все нефинальные этапы'}, ...visibleStages]
            : visibleStages
    ), [visibleStages]);

    // --- Resume loading ---
    const loadResumes = useCallback(async () => {
        if (!vacancyId) return;
        if (!filtersApplied) {
            setResumes([]);
            return;
        }
        setListLoading(true);
        try {
            const params = {vacancy_b24_element_id: vacancyId};
            if (effectiveDirection) params.direction_id = effectiveDirection.direction_id;
            if (effectiveStages.length === 1) params.stage_id = effectiveStages[0].stage_id;

            const resumesResponse = await api.get('/analysis_agent/applicant/resumes', {params});
            let items = resumesResponse.data || [];

            if (effectiveStages.length > 1) {
                items = items.filter((item) => selectedStageIds.has(item.stage_id));
            } else if (effectiveStages.length === 0) {
                items = items.filter((item) => nonFinalStageIds.has(item.stage_id));
            }

            setResumes(items);
            setSelectedIds(new Set());

            // Fetch flags for already evaluated resumes
            const evaluatedIds = items
                .filter((item) => item.ai_status === 'created' || item.ai_status === 'error')
                .map((item) => item.crm_item_id);
            if (evaluatedIds.length > 0) {
                try {
                    const resultsResponse = await api.post('/analysis_agent/applicant/evaluate/batch/results', {
                        crm_item_ids: evaluatedIds,
                    });
                    const nextMap = {};
                    (resultsResponse.data?.items || []).forEach((r) => {
                        nextMap[r.crm_item_id] = r;
                    });
                    setResultById((prev) => ({...prev, ...nextMap}));
                } catch (_) {
                    // non-critical, flags just won't show
                }
            }

            BX24.fitWindow();
        } catch (e) {
            handleError('Ошибка загрузки резюме', e);
        } finally {
            setListLoading(false);
        }
    }, [api, vacancyId, filtersApplied, effectiveDirection, effectiveStages, selectedStageIds, nonFinalStageIds, handleError, BX24]);

    useEffect(() => {
        if (!vacancyId || Object.keys(allStagesByDirection).length === 0) return;
        loadResumes();
    }, [vacancyId, allStagesByDirection, effectiveDirection, effectiveStages, loadResumes]);

    // --- Filtered & sorted resumes ---
    const filteredResumes = useMemo(() => {
        let items = [...resumes];

        items = items.map((item) => {
            const result = resultById[item.crm_item_id];
            return {
                ...item,
                effectiveScore: result?.score ?? item.ai_score,
                effectiveStatus: result?.status || item.ai_status || 'not_created',
            };
        });

        if (hideEvaluated) {
            items = items.filter((item) => item.effectiveStatus !== 'created');
        }

        const fromVal = threshold.from === '' ? null : Number(threshold.from);
        const toVal = threshold.to === '' ? null : Number(threshold.to);
        if (fromVal !== null || toVal !== null) {
            items = items.filter((item) => {
                const score = toSortableScore(item.effectiveScore);
                if (score === null) return true;
                if (fromVal !== null && score < fromVal) return false;
                if (toVal !== null && score > toVal) return false;
                return true;
            });
        }

        items.sort((a, b) => {
            const scoreA = toSortableScore(a.effectiveScore);
            const scoreB = toSortableScore(b.effectiveScore);
            if (scoreA === null && scoreB === null) return a.crm_item_id - b.crm_item_id;
            if (scoreA === null) return 1;
            if (scoreB === null) return -1;
            if (scoreB !== scoreA) return scoreB - scoreA;
            return a.crm_item_id - b.crm_item_id;
        });

        return items;
    }, [resumes, resultById, hideEvaluated, threshold]);

    // --- Toggle helpers ---
    // Clean up selectedIds when filtered list changes
    useEffect(() => {
        const visibleIds = new Set(filteredResumes.map((item) => item.crm_item_id));
        setSelectedIds((prev) => {
            const next = new Set([...prev].filter((id) => visibleIds.has(id)));
            if (next.size === prev.size) return prev;
            return next;
        });
    }, [filteredResumes]);

    const toggleResume = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedIds((prev) => {
            if (prev.size === filteredResumes.length && prev.size > 0) return new Set();
            return new Set(filteredResumes.map((item) => item.crm_item_id));
        });
    };

    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const persistStageFilter = (stageOptions) => {
        const normalized = Array.isArray(stageOptions) ? stageOptions : (stageOptions ? [stageOptions] : []);
        setSelectedStages(normalized);
    };

    const selectByScore = () => {
        const from = threshold.from === '' ? 1 : Number(threshold.from);
        const to = threshold.to === '' ? 10 : Number(threshold.to);
        if (!Number.isFinite(from) || !Number.isFinite(to) || from > to) {
            setNotificationType('error');
            setNotificationText('Введите корректный диапазон оценок');
            return;
        }
        const picked = filteredResumes
            .filter((item) => {
                const score = toSortableScore(item.effectiveScore);
                if (score === null) return false;
                return score >= from && score <= to;
            })
            .map((item) => item.crm_item_id);
        setSelectedIds(new Set(picked));
    };

    // --- CRM link helpers ---
    const getResumePath = useCallback((crmItemId) => {
        if (!portalSettings) return null;
        const type = portalSettings.smart_process_type;
        if (type === 'lead') return `/crm/lead/details/${crmItemId}/`;
        if (type === 'deal') return `/crm/deal/details/${crmItemId}/`;
        if (type === 'smart_process' && portalSettings.smart_process_id) {
            return `/crm/type/${portalSettings.smart_process_id}/details/${crmItemId}/`;
        }
        return null;
    }, [portalSettings]);

    const openResumeCard = useCallback((crmItemId) => {
        const path = getResumePath(crmItemId);
        if (!path) {
            setNotificationType('error');
            setNotificationText('Не удалось построить ссылку на карточку резюме');
            return;
        }
        if (typeof BX24?.openPath === 'function') {
            BX24.openPath(path);
            return;
        }
        window.open(path, '_blank', 'noopener,noreferrer');
    }, [BX24, getResumePath]);

    const openFullAnalysis = useCallback((crmItemId) => {
        const ids = resumes.map((item) => item.crm_item_id).join(',');
        const params = new URLSearchParams({
            crm_item_id: String(crmItemId),
            return_to: 'resume_workspace',
            vacancy_id: String(vacancyId ?? ''),
            ids,
        });
        if (effectiveDirection?.direction_id !== undefined && effectiveDirection?.direction_id !== null) {
            params.set('direction_id', String(effectiveDirection.direction_id));
        }
        if (effectiveStages.length) {
            params.set('stage_ids', effectiveStages.map((s) => s.stage_id).join(','));
        }
        navigate(`/applicant_analys?${params.toString()}`);
    }, [navigate, resumes, vacancyId, effectiveDirection, effectiveStages]);

    // --- Re-evaluate toggle ---
    const [includeEvaluated, setIncludeEvaluated] = useState(false);

    // --- Batch evaluate (inline, no navigation) ---
    const startEvaluate = async (forceReanalyze) => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            setNotificationType('error');
            setNotificationText('Выберите хотя бы одно резюме');
            return;
        }
        setActionLoading(true);
        try {
            await api.post('/analysis_agent/applicant/evaluate/batch', {
                crm_item_ids: ids,
                vacancy_b24_element_id: vacancyId,
                force_reanalyze: forceReanalyze,
            });
            setEvaluatingIds(ids);
            setNotificationType('success');
            setNotificationText(`Запущена оценка ${ids.length} резюме`);
        } catch (e) {
            handleError('Ошибка запуска оценки', e);
        } finally {
            setActionLoading(false);
        }
    };

    // --- Polling ---
    const fetchResults = useCallback(async () => {
        if (!evaluatingIds.length) return;
        try {
            const response = await api.post('/analysis_agent/applicant/evaluate/batch/results', {
                crm_item_ids: evaluatingIds,
            });
            const nextMap = {};
            (response.data?.items || []).forEach((item) => {
                nextMap[item.crm_item_id] = item;
            });
            setResultById((prev) => ({...prev, ...nextMap}));

            const needsPolling = (response.data?.items || []).some((item) =>
                item.status === 'creating' || item.status === 'updating' || item.status === 'not_created'
            );

            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
            if (needsPolling) {
                pollRef.current = setTimeout(fetchResults, 4000);
            } else {
                await loadResumes();
            }
        } catch (e) {
            handleError('Ошибка получения результатов оценки', e);
        }
    }, [api, evaluatingIds, handleError, loadResumes]);

    useEffect(() => {
        if (evaluatingIds.length === 0) return;
        fetchResults();
        return () => {
            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [evaluatingIds, fetchResults]);

    // --- Stage movement ---
    const selectedDirectionsUnique = useMemo(() => {
        const dirs = resumes
            .filter((item) => selectedIds.has(item.crm_item_id))
            .map((item) => item.direction_id)
            .filter((v) => v !== null && v !== undefined);
        return Array.from(new Set(dirs));
    }, [resumes, selectedIds]);

    const hasMixedDirections = !effectiveDirection && selectedDirectionsUnique.length > 1;

    const visibleTargetStages = useMemo(() => {
        if (effectiveDirection) {
            return allStagesByDirection[effectiveDirection.direction_id] || [];
        }
        if (selectedDirectionsUnique.length === 1) {
            return allStagesByDirection[selectedDirectionsUnique[0]] || [];
        }
        return [];
    }, [effectiveDirection, allStagesByDirection, selectedDirectionsUnique]);

    const moveSelected = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            setNotificationType('error');
            setNotificationText('Выберите хотя бы одно резюме');
            return;
        }
        if (!selectedTargetStage) {
            setNotificationType('error');
            setNotificationText('Выберите этап назначения');
            return;
        }
        setActionLoading(true);
        try {
            await api.post('/analysis_agent/applicant/move-to-stage', {
                crm_item_ids: ids,
                stage_id: selectedTargetStage.stage_id,
            });
            setNotificationType('success');
            setNotificationText(`Перенесено резюме: ${ids.length}`);
            await loadResumes();
        } catch (e) {
            handleError('Ошибка переноса резюме', e);
        } finally {
            setActionLoading(false);
        }
    };

    // --- Render ---
    return (
        <div className={s.page}>
            <div className={s.headerWrap}>
                <Header/>
            </div>
            <div className={s.scrollArea}>
            <div className={s.container}>
                <h1>Оценка кандидатов</h1>
                <InlineNotification
                    type={notificationType}
                    text={notificationText}
                    hideFlag={!notificationText}
                />

                {/* Filters */}
                {!metaLoading && (
                    <>
                        <div className={`${s.filterRow} ${s.compactSelectRow}`}>
                            <OneSelectUI
                                title={'Направление'}
                                selectionOptions={directionOptionsWithAll}
                                selectedOption={selectedDirection}
                                setSelectedOption={(value) => {
                                    const nextDirection = value || null;
                                    setSelectedDirection(nextDirection);
                                    setSelectedStages([]);
                                    // Сброс до "ничего не выбрано" ⇒ снимаем filtersApplied
                                    setFiltersApplied(!!nextDirection);
                                }}
                                isClearable={true}
                                placeholder={'Выберите направление'}
                            />
                            <OneSelectUI
                                title={'Этап'}
                                selectionOptions={stageOptionsWithAll}
                                selectedOption={selectedStages}
                                setSelectedOption={(value) => {
                                    const arr = Array.isArray(value) ? value : (value ? [value] : []);
                                    // Если юзер добавил sentinel к уже выбранным реальным — считаем sentinel единственным выбором
                                    const nextArr = arr.some((v) => v && v.value === ALL_NON_FINAL_STAGES_VALUE)
                                        ? [ALL_NON_FINAL_STAGES_OPTION]
                                        : arr;
                                    setSelectedStages(nextArr);
                                    // Сброс до пустого — если direction тоже не выбран, снимаем filtersApplied
                                    if (nextArr.length) {
                                        setFiltersApplied(true);
                                    } else if (!selectedDirection) {
                                        setFiltersApplied(false);
                                    }
                                }}
                                isMulti={true}
                                closeMenuOnSelect={false}
                                isClearable={true}
                                placeholder={'Выберите этап'}
                            />
                        </div>

                        {/* Toolbar — always visible */}
                        <div className={s.actionsButtons}>
                            <label className={s.filterCheckboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={hideEvaluated}
                                    onChange={(e) => setHideEvaluated(e.target.checked)}
                                />
                                Скрыть оцененных
                            </label>

                            <div className={s.thresholdPanel}>
                                <div className={s.thresholdTitle}>Оценка</div>
                                <div className={s.thresholdRange}>
                                    <input
                                        className={`${s.numberInput} ${s.thresholdInput}`}
                                        placeholder="от"
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={threshold.from}
                                        onChange={(e) => setThreshold((prev) => ({...prev, from: e.target.value}))}
                                    />
                                    <span className={s.thresholdDash}>—</span>
                                    <input
                                        className={`${s.numberInput} ${s.thresholdInput}`}
                                        placeholder="до"
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={threshold.to}
                                        onChange={(e) => setThreshold((prev) => ({...prev, to: e.target.value}))}
                                    />
                                </div>
                                <button className={s.secondaryButton} onClick={selectByScore}>Выбрать</button>
                            </div>

                            <button className={s.secondaryButton} onClick={toggleAll}>
                                {selectedIds.size === filteredResumes.length && selectedIds.size > 0 ? 'Снять выбор' : 'Выбрать все'}
                            </button>

                            <div className={s.selectionCounter}>
                                Выбрано {selectedIds.size} из {filteredResumes.length}
                            </div>
                        </div>

                        {/* Evaluating indicator */}
                        {evaluatingIds.length > 0 && (
                            <div className={s.evaluatingIndicator}>
                                <span>
                                    Оценка выполняется...
                                    {(() => {
                                        const done = evaluatingIds.filter((id) => {
                                            const st = resultById[id]?.status;
                                            return st === 'created' || st === 'error';
                                        }).length;
                                        return ` ${done}/${evaluatingIds.length} готово`;
                                    })()}
                                </span>
                                <button
                                    type="button"
                                    className={s.evaluatingClose}
                                    onClick={() => {
                                        if (pollRef.current) {
                                            clearTimeout(pollRef.current);
                                            pollRef.current = null;
                                        }
                                        setEvaluatingIds([]);
                                    }}
                                    aria-label="Закрыть"
                                >
                                    &times;
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Skeleton loading */}
                {(metaLoading || listLoading) && (
                    <div className={s.skeletonList}>
                        {Array.from({length: 6}).map((_, i) => (
                            <div key={i} className={s.skeletonRow}>
                                <div className={`${s.skeletonPulse} ${s.skeletonCircle}`}/>
                                <div className={`${s.skeletonPulse} ${s.skeletonText}`}/>
                                <div className={`${s.skeletonPulse} ${s.skeletonTextShort}`}/>
                            </div>
                        ))}
                    </div>
                )}

                {/* Resume list */}
                {!metaLoading && !listLoading && (
                    <div className={s.dashboardList}>
                        {filteredResumes.map((item) => {
                            const result = resultById[item.crm_item_id] || {};
                            const statusMeta = getStatusMeta(item.effectiveStatus);
                            const scoreMeta = getScoreMeta(item.effectiveScore);
                            const displayName = buildFullName(item);
                            const desiredPosition = item.desired_position ?? '—';
                            const desiredSalary = item.desired_salary ?? '—';
                            const isExpanded = expandedIds.has(item.crm_item_id);
                            const hasDetails = result.details || result.green_flags || result.red_flags || item.effectiveStatus === 'created';

                            return (
                                <div key={item.crm_item_id} className={s.dashboardRow}>
                                    <div className={s.dashboardMain}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(item.crm_item_id)}
                                            onChange={() => toggleResume(item.crm_item_id)}
                                        />
                                        <div
                                            className={s.scoreCircleSmall}
                                            style={{
                                                background: `conic-gradient(${scoreMeta.color} ${scoreMeta.progressDeg}deg, #E2E8F0 0deg)`,
                                            }}
                                        >
                                            <div className={s.scoreCircleSmallInner}>{scoreMeta.value}</div>
                                        </div>

                                        <div className={s.dashboardCell}>
                                            <div className={s.dashboardCaption}>ФИО</div>
                                            <div className={s.cardTitle}>{displayName}</div>
                                        </div>

                                        <div className={s.dashboardCell}>
                                            <div className={s.dashboardCaption}>Должность</div>
                                            <div className={s.cardMeta}>{desiredPosition}</div>
                                        </div>

                                        <div className={s.dashboardCell}>
                                            <div className={s.dashboardCaption}>Желаемая ЗП</div>
                                            <div className={s.cardMeta}>{desiredSalary}</div>
                                        </div>
                                    </div>

                                    <div className={s.dashboardActions}>
                                        <span className={`${s.statusBadge} ${statusMeta.className}`}>
                                            {statusMeta.label}
                                        </span>
                                        <button
                                            type="button"
                                            className={s.secondaryButton}
                                            onClick={() => openResumeCard(item.crm_item_id)}
                                        >
                                            В CRM
                                        </button>
                                        {item.effectiveStatus === 'created' && (
                                            <button
                                                type="button"
                                                className={s.secondaryButton}
                                                onClick={() => openFullAnalysis(item.crm_item_id)}
                                            >
                                                Полный анализ
                                            </button>
                                        )}
                                        {hasDetails && (
                                            <button
                                                type="button"
                                                className={s.expandChevron}
                                                onClick={() => toggleExpanded(item.crm_item_id)}
                                                aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
                                            >
                                                <span className={`${s.expandArrow} ${isExpanded ? s.expandArrowOpen : ''}`}/>
                                            </button>
                                        )}
                                    </div>

                                    {hasDetails && (
                                        <div className={`${s.dashboardExpandedWrap} ${isExpanded ? s.dashboardExpandedWrapOpen : ''}`}>
                                            <div className={s.dashboardExpanded}>
                                                {(() => {
                                                    const details = result.details;
                                                    if (details) {
                                                        const goodNames = [
                                                            ...(details.competency_scores || []).filter(i => i.status === 'ok' && i.comment).map(i => i.comment),
                                                            ...(details.constraint_checks || []).filter(i => i.status === 'ok' && i.comment).map(i => i.comment),
                                                        ];
                                                        const badNames = [
                                                            ...(details.competency_scores || []).filter(i => i.status === 'missing' && i.comment).map(i => i.comment),
                                                            ...(details.constraint_checks || []).filter(i => i.status === 'violated' && i.comment).map(i => i.comment),
                                                        ];
                                                        return (
                                                            <>
                                                                {goodNames.length > 0 && (
                                                                    <div className={s.goodText}>Плюсы: {goodNames.join('; ')}</div>
                                                                )}
                                                                {badNames.length > 0 && (
                                                                    <div className={s.badText}>Минусы: {badNames.join('; ')}</div>
                                                                )}
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <>
                                                            {result.green_flags?.length > 0 && (
                                                                <div className={s.goodText}>Плюсы: {result.green_flags.join('; ')}</div>
                                                            )}
                                                            {result.red_flags?.length > 0 && (
                                                                <div className={s.badText}>Минусы: {result.red_flags.join('; ')}</div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!metaLoading && !listLoading && filteredResumes.length === 0 && (
                    <div className={s.empty}>
                        {filtersApplied
                            ? 'По текущему фильтру резюме не найдено'
                            : 'Выберите направление и этап, чтобы загрузить кандидатов'}
                    </div>
                )}
            </div>
            </div>

            {/* Sticky bottom action bar */}
            <div className={`${s.stickyBar} ${selectedIds.size > 0 ? s.stickyBarOpen : ''}`}>
                <div className={s.stickyBarOverflow}>
                    <div className={s.stickyBarInner}>
                        <div className={s.stickyBarCount}>
                            Выбрано {selectedIds.size}
                        </div>

                        <div className={s.stickyBarDivider}/>

                        <SubmitButton
                            text="Оценить"
                            onClick={() => startEvaluate(includeEvaluated)}
                            disabled={actionLoading}
                        />
                        <label className={s.filterCheckboxLabel}>
                            <input
                                type="checkbox"
                                checked={includeEvaluated}
                                onChange={(e) => setIncludeEvaluated(e.target.checked)}
                            />
                            Вкл. оцененных
                        </label>

                        {hasMixedDirections && (
                            <>
                                <div className={s.stickyBarDivider}/>
                                <div className={s.stickyBarHint}>
                                    Перенос невозможен: выбраны кандидаты из разных направлений
                                </div>
                            </>
                        )}

                        {!hasMixedDirections && visibleTargetStages.length > 0 && (
                            <>
                                <div className={s.stickyBarDivider}/>
                                <div className={s.stickyBarMove}>
                                    <OneSelectUI
                                        selectionOptions={visibleTargetStages}
                                        selectedOption={selectedTargetStage}
                                        setSelectedOption={(v) => setSelectedTargetStage(v || null)}
                                        isClearable={true}
                                        placeholder={'Этап'}
                                        menuPlacement="top"
                                    />
                                    <SubmitButton
                                        text="Перенести"
                                        onClick={moveSelected}
                                        disabled={actionLoading}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
